/**
 * OpenChallengeCard — one takeable challenge on the public board (Arena → Open Board).
 *
 * Two columns, per the Open Challenges design. LEFT is the poster, rendered big: the avatar is the
 * subject of the card, standing on a plinth ringed in their rank colour, with the rank crest and
 * level hanging off its corners and the name + tier underneath. RIGHT is what you are signing up
 * for: entry, time to kickoff, the slate, the payout, and the button.
 *
 * The plinth is a rounded square, not a circle: our avatars are full-body characters and a circular
 * mask clips their arms and feet.
 *
 * specs/open-challenges AC-6/AC-17a.
 */

import React, { useMemo } from 'react';
import {
  View, Text, Pressable, Platform,
} from 'react-native';
import { Image } from 'expo-image';
import { Target, Clock } from 'lucide-react-native';
import { FriendAvatar } from '../../friends/shared/FriendAvatar';
import { PlayerRankBadge } from '../../../src/economy/components/PlayerRankBadge/PlayerRankBadge';
import { getRank, getRankAccentColor } from '../../../src/economy/helpers/rankRegistry.helper';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { isAvatarUrl } from '../../../src/create/helpers/createUi.helper';
import { normalizeDisplayAvatarId } from '../../../src/avatars/types/avatar.types';
import {
  ASSET_PATHS, getCdnUrlExactKey, IDLE_COIN_SOURCE,
} from '../../../constants/assets';
import {
  OPEN_BOARD_PAYOUT_MULTIPLIER,
  OPEN_BOARD_QUICK_MAX_PICKS,
} from '../../../src/challenges/constants/openBoard.constants';
import { isClosingSoon, minutesToKickoff } from '../../../src/challenges/helpers/openBoardBrowse.helper';
import type { OpenBoardCard } from '../../../src/challenges/types/openBoard.types';
import {
  getOpenChallengeCardStyles,
  OPEN_CARD_AVATAR_SIZE,
  OPEN_CARD_BADGE_SIZE,
} from './OpenChallengeCard.styles';

interface OpenChallengeCardProps {
  card: OpenBoardCard;
  onTakeOn: (challengeId: string) => void;
  /** Injectable clock so stories and tests are not wall-clock dependent. */
  now?: number;
  testID?: string;
}

function creatorName(creator: OpenBoardCard['creator']): string {
  const full = `${creator.firstName ?? ''} ${creator.lastName ?? ''}`.trim();
  return full || creator.username || 'Player';
}

/** Compact countdown to the first kickoff: 42m · 3h 20m · 2d. */
export function formatClosesIn(minutes: number | null): string {
  if (minutes == null) return 'Upcoming';
  if (minutes <= 0) return 'Starting';
  if (minutes < 60) return `${minutes}m`;
  if (minutes < 1440) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }
  return `${Math.floor(minutes / 1440)}d`;
}

const formatCoins = (n: number): string => n.toLocaleString('en-US');

export const OpenChallengeCard: React.FC<OpenChallengeCardProps> = ({
  card, onTakeOn, now = Date.now(), testID,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getOpenChallengeCardStyles(theme), [theme]);
  const crownUri = getCdnUrlExactKey(ASSET_PATHS.CROWN_ICON);

  const name = creatorName(card.creator);
  const rankIndex = card.creator?.rank ?? null;
  const level = card.creator?.level ?? null;
  const rankName = rankIndex != null ? getRank(rankIndex).name : null;
  // Unranked posters keep the neutral ring from the stylesheet rather than borrowing a tier's
  // colour, so a colour on this card always means a rank the server actually derived.
  const accent = rankIndex != null ? getRankAccentColor(rankIndex) : null;
  const plinthRing = accent != null ? { borderColor: accent } : null;

  const urgent = isClosingSoon(card, now);
  const closesLabel = formatClosesIn(minutesToKickoff(card.startDate, now));
  const stakeLabel = card.stakeCoins > 0 ? formatCoins(card.stakeCoins) : 'Free';
  // A free challenge has nothing to win, so the whole payout row is omitted rather than filled
  // with a stand-in. The entry chip already reads "Free", which says it.
  const hasPayout = card.stakeCoins > 0;
  // No "+" prefix: this is the total credited to the winner, stake included, not the profit.
  const payoutLabel = formatCoins(Math.round(card.stakeCoins * OPEN_BOARD_PAYOUT_MULTIPLIER));
  const isBigSlate = card.matchCount > OPEN_BOARD_QUICK_MAX_PICKS;

  return (
    <Pressable
      onPress={() => onTakeOn(card.id)}
      testID={testID ?? `open-challenge-card-${card.id}`}
      accessibilityRole="button"
      accessibilityLabel={`Take on ${name}'s challenge`}
      style={({ pressed, hovered }) => [
        styles.card,
        urgent && styles.cardUrgent,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        hovered && Platform.OS === 'web' && styles.hover,
        pressed && styles.pressed,
      ]}
    >
      {/* ── the poster ── */}
      <View style={styles.heroColumn}>
        <View style={styles.plinthWrap}>
          <View style={[styles.plinth, plinthRing]}>
            <FriendAvatar
              avatarId={normalizeDisplayAvatarId(card.creator.avatarId)}
              fallbackUrl={isAvatarUrl(card.creator.avatar ?? '') ? card.creator.avatar : undefined}
              size={OPEN_CARD_AVATAR_SIZE}
              nameForInitials={name}
              avatarRounded={false}
            />
          </View>
          {rankIndex != null && (
            <PlayerRankBadge
              rankIndex={rankIndex}
              size={OPEN_CARD_BADGE_SIZE}
              style={styles.rankBadge}
            />
          )}
          {level != null && accent != null && (
            <View style={[styles.levelPill, { backgroundColor: accent }]}>
              <Text style={styles.levelPillText}>LV {level}</Text>
            </View>
          )}
        </View>

        <View style={styles.heroIdentity}>
          <Text style={styles.name} numberOfLines={1}>{name}</Text>
          {rankName != null && accent != null && (
            <Text style={[styles.tierLabel, { color: accent }]} numberOfLines={1}>
              {rankName.toUpperCase()}
            </Text>
          )}
        </View>
      </View>

      {/* ── the challenge ── */}
      <View style={styles.detailColumn}>
        <View style={styles.entryRow}>
          <View style={styles.entryBlock}>
            <Text style={styles.entryLabel}>ENTRY</Text>
            <View style={styles.entryValueRow}>
              <Text style={styles.entryValue} numberOfLines={1}>{stakeLabel}</Text>
              {card.stakeCoins > 0 && (
                <Image
                  source={IDLE_COIN_SOURCE}
                  style={styles.coinIcon}
                  contentFit="contain"
                  alt={WEB_IMAGE_ALT_DECORATIVE}
                />
              )}
            </View>
          </View>
          <View style={styles.clockPill}>
            <Clock size={12} color={urgent ? theme.error : theme.gray[500]} strokeWidth={2} />
            <Text style={[styles.clockText, urgent && styles.clockTextUrgent]}>{closesLabel}</Text>
          </View>
        </View>

        <View style={styles.metaPanel}>
          <View style={styles.picksRow}>
            <Target size={13} color={theme.primary} strokeWidth={2} />
            <Text style={styles.picksValue}>{card.matchCount}</Text>
            <Text style={styles.picksLabel}>{card.matchCount === 1 ? 'pick' : 'picks'}</Text>
            {/* A slate past the free allowance — the crown is the same one Pro wears elsewhere. */}
            {isBigSlate && (
              <Image
                source={{ uri: crownUri }}
                style={styles.bigSlateCrown}
                contentFit="contain"
                tintColor={theme.accent}
                testID="open-challenge-card-big-slate"
                alt={WEB_IMAGE_ALT_DECORATIVE}
              />
            )}
          </View>
          {card.leagueName != null && (
            <Text style={styles.league} numberOfLines={1}>{card.leagueName}</Text>
          )}
          {hasPayout && (
            <View style={styles.payoutRow}>
              <Text style={styles.payoutLabel}>PAYOUT</Text>
              <Text style={styles.payoutValue}>{payoutLabel}</Text>
              <Image
                source={IDLE_COIN_SOURCE}
                style={styles.payoutCoin}
                contentFit="contain"
                alt={WEB_IMAGE_ALT_DECORATIVE}
              />
            </View>
          )}
        </View>

        <View style={styles.takeBtn}>
          <Text style={styles.takeBtnText}>Take on</Text>
        </View>
      </View>
    </Pressable>
  );
};
